import {useEffect, useMemo, useState} from 'react';

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';

import {
    C,
    DEFAULT_PROFILE_SECTION_SETTINGS,
    PROFILE_HEADING_STYLE_OPTIONS,
    PROFILE_WRITING_STYLE_OPTIONS,
    TONE_OPTIONS,
    USECASE_OPTIONS,
    mergeProfileSettings,
} from '../constants';

const SECTION_LIMITS = {
    title: 300,
    description: 2500,
    bullets: 500,
};

// FAQ char caps are split because the two fields have very different
// natural lengths — questions are short, answers can be a paragraph.
const FAQ_QUESTION_CAP = 200;
const FAQ_ANSWER_CAP = 1000;
const FAQ_COUNT_MIN = 1;
const FAQ_COUNT_MAX = 10;

const PROFILE_CONTEXT_MAX_CHARS = 2000;

const gridSx = {
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, minmax(0, 1fr))',
    },
    gap: 1.5,
};

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        backgroundColor: C.surface,
    },
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const RangeInput = ({label, value, maxLimit, onCommit, sx}) => {
    const [raw, setRaw] = useState(String(value));

    useEffect(() => {
        setRaw(String(value));
    }, [value]);

    const handleChange = (event) => {
        const next = event.target.value;
        setRaw(next);
        if (next === '') return;
        const parsed = Number(next);
        if (!Number.isFinite(parsed)) return;
        onCommit(parsed, false);
    };

    const handleBlur = () => {
        const parsed = Number(raw);
        if (raw === '' || !Number.isFinite(parsed)) {
            setRaw(String(value));
            return;
        }
        onCommit(parsed, true);
    };

    return (
        <TextField
            label={label}
            type="number"
            size="small"
            value={raw}
            inputProps={{min: 1, max: maxLimit}}
            onChange={handleChange}
            onBlur={handleBlur}
            sx={sx}
        />
    );
};

const ProfileSettingsDialog = ({open, profile, onClose, onSave}) => {
    const [activeTab, setActiveTab] = useState('bullets');
    const [profileName, setProfileName] = useState('');
    const [settings, setSettings] = useState(() => mergeProfileSettings(profile?.settings));
    const mergedSettings = useMemo(() => mergeProfileSettings(settings), [settings]);

    useEffect(() => {
        if (!open) return;
        setActiveTab('general');
        setProfileName(profile?.name || '');
        setSettings(mergeProfileSettings(profile?.settings));
    }, [open, profile]);

    const updateSection = (section, key, value) => {
        setSettings((currentSettings) => ({
            ...mergeProfileSettings(currentSettings),
            [section]: {
                ...mergeProfileSettings(currentSettings)[section],
                [key]: value,
            },
        }));
    };

    const updateRange = (section, nextMinValue, nextMaxValue, {enforceOrder = false} = {}) => {
        const maxLimit = SECTION_LIMITS[section];
        const currentSection = mergedSettings[section];
        const min = clamp(
            Number(
                nextMinValue ??
                    currentSection.minChars ??
                    DEFAULT_PROFILE_SECTION_SETTINGS[section].minChars,
            ),
            1,
            maxLimit,
        );
        let max = clamp(
            Number(
                nextMaxValue ??
                    currentSection.maxChars ??
                    DEFAULT_PROFILE_SECTION_SETTINGS[section].maxChars,
            ),
            1,
            maxLimit,
        );
        if (enforceOrder && max < min) {
            max = min;
        }

        updateSection(section, 'minChars', min);
        updateSection(section, 'maxChars', max);
    };

    const handleSave = () => {
        if (!profileName.trim()) return;

        onSave({
            ...profile,
            name: profileName.trim(),
            settings: mergedSettings,
        });
    };

    const renderRangeSection = (section, label, helperText) => (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                pb: 2,
                borderBottom: `1px solid ${C.border}`,
            }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                }}>
                <Typography sx={{fontSize: 14, fontWeight: 700, color: C.ink}}>{label}</Typography>
                <Typography sx={{fontSize: 12, color: C.textMuted}}>
                    {mergedSettings[section].minChars} - {mergedSettings[section].maxChars} chars
                </Typography>
            </Box>
            <Box sx={gridSx}>
                <RangeInput
                    label="Min characters"
                    value={mergedSettings[section].minChars}
                    maxLimit={SECTION_LIMITS[section]}
                    onCommit={(next, isBlur) =>
                        updateRange(section, next, mergedSettings[section].maxChars, {
                            enforceOrder: isBlur,
                        })
                    }
                    sx={textFieldSx}
                />
                <RangeInput
                    label="Max characters"
                    value={mergedSettings[section].maxChars}
                    maxLimit={SECTION_LIMITS[section]}
                    onCommit={(next, isBlur) =>
                        updateRange(section, mergedSettings[section].minChars, next, {
                            enforceOrder: isBlur,
                        })
                    }
                    sx={textFieldSx}
                />
            </Box>
            <Typography sx={{fontSize: 12, lineHeight: 1.6, color: C.textMuted}}>
                {helperText}
            </Typography>
        </Box>
    );

    // FAQs carry two ranges (question + answer) instead of one — this
    // helper renders a labeled min/max pair against custom keys on the
    // faqs section, mirroring the visual treatment of renderRangeSection.
    const updateFaqRange = (minKey, maxKey, nextMinValue, nextMaxValue, cap, enforceOrder = false) => {
        const min = clamp(
            Number(
                nextMinValue ??
                    mergedSettings.faqs[minKey] ??
                    DEFAULT_PROFILE_SECTION_SETTINGS.faqs[minKey],
            ),
            1,
            cap,
        );
        let max = clamp(
            Number(
                nextMaxValue ??
                    mergedSettings.faqs[maxKey] ??
                    DEFAULT_PROFILE_SECTION_SETTINGS.faqs[maxKey],
            ),
            1,
            cap,
        );
        if (enforceOrder && max < min) max = min;
        updateSection('faqs', minKey, min);
        updateSection('faqs', maxKey, max);
    };

    const renderFaqRangePair = ({label, minKey, maxKey, cap, helperText}) => (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                pb: 2,
                borderBottom: `1px solid ${C.border}`,
            }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                }}>
                <Typography sx={{fontSize: 14, fontWeight: 700, color: C.ink}}>
                    {label}
                </Typography>
                <Typography sx={{fontSize: 12, color: C.textMuted}}>
                    {mergedSettings.faqs[minKey]} - {mergedSettings.faqs[maxKey]} chars
                </Typography>
            </Box>
            <Box sx={gridSx}>
                <RangeInput
                    label="Min characters"
                    value={mergedSettings.faqs[minKey]}
                    maxLimit={cap}
                    onCommit={(next, isBlur) =>
                        updateFaqRange(
                            minKey,
                            maxKey,
                            next,
                            mergedSettings.faqs[maxKey],
                            cap,
                            isBlur,
                        )
                    }
                    sx={textFieldSx}
                />
                <RangeInput
                    label="Max characters"
                    value={mergedSettings.faqs[maxKey]}
                    maxLimit={cap}
                    onCommit={(next, isBlur) =>
                        updateFaqRange(
                            minKey,
                            maxKey,
                            mergedSettings.faqs[minKey],
                            next,
                            cap,
                            isBlur,
                        )
                    }
                    sx={textFieldSx}
                />
            </Box>
            <Typography sx={{fontSize: 12, lineHeight: 1.6, color: C.textMuted}}>
                {helperText}
            </Typography>
        </Box>
    );

    const renderToneStyleFields = (section) => (
        <Box sx={gridSx}>
            <TextField
                select
                label="Default tone"
                size="small"
                value={mergedSettings[section].defaultTone}
                onChange={(event) => updateSection(section, 'defaultTone', event.target.value)}
                sx={textFieldSx}>
                {TONE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                        {option}
                    </MenuItem>
                ))}
            </TextField>
            <TextField
                select
                label="Default writing style"
                size="small"
                value={mergedSettings[section].defaultWritingStyle}
                onChange={(event) =>
                    updateSection(section, 'defaultWritingStyle', event.target.value)
                }
                sx={textFieldSx}>
                {PROFILE_WRITING_STYLE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </TextField>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    backgroundColor: C.surface,
                    backgroundImage: 'none',
                },
            }}>
            <DialogTitle
                sx={{
                    px: 3,
                    py: 2,
                    color: '#fff',
                    background: 'linear-gradient(135deg, #1d9e75, #185fa5)',
                }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}>
                    <Box>
                        <Typography sx={{fontSize: 16, fontWeight: 700}}>
                            Profile: Content Settings
                        </Typography>
                        <Typography sx={{mt: 0.5, fontSize: 13, opacity: 0.88}}>
                            Configure title, description, bullet, and FAQ defaults for this profile.
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'inherit',
                            backgroundColor: 'rgba(255,255,255,.18)',
                            '&:hover': {
                                backgroundColor: 'rgba(255,255,255,.24)',
                            },
                        }}>
                        ×
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{px: 3, py: 2.5, backgroundColor: C.surface}}>
                <Tabs
                    value={activeTab}
                    onChange={(_, nextTab) => setActiveTab(nextTab)}
                    variant="scrollable"
                    allowScrollButtonsMobile
                    sx={{
                        mb: 2,
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            minHeight: 42,
                            fontWeight: 600,
                        },
                    }}>
                    <Tab value="general" label="General" />
                    <Tab value="bullets" label="Bullet points" />
                    <Tab value="faqs" label="FAQs" />
                    <Tab value="description" label="Description" />
                    <Tab value="title" label="Title" />
                </Tabs>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        p: 2,
                        border: `1px solid ${C.border}`,
                        borderRadius: 2.5,
                        backgroundColor: C.surfaceAlt,
                    }}>
                    {activeTab === 'general' && (
                        <>
                            <TextField
                                select
                                label="Use case"
                                size="small"
                                value={mergedSettings.usecase}
                                onChange={(event) =>
                                    setSettings((s) => ({...mergeProfileSettings(s), usecase: event.target.value}))
                                }
                                sx={textFieldSx}>
                                {USECASE_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                pb: 2,
                                borderBottom: `1px solid ${C.border}`,
                            }}>
                                <Typography sx={{fontSize: 14, fontWeight: 700, color: C.ink}}>Tone override</Typography>
                                <Typography sx={{fontSize: 12, color: C.textMuted, lineHeight: 1.5}}>
                                    Leave empty to inherit from your brand profile. Set a value to override for this specific use case.
                                </Typography>
                                <TextField
                                    select
                                    label={mergedSettings.toneOverride ? 'Overridden' : 'Inherited from brand'}
                                    size="small"
                                    value={mergedSettings.toneOverride || ''}
                                    onChange={(event) =>
                                        setSettings((s) => ({...mergeProfileSettings(s), toneOverride: event.target.value || null}))
                                    }
                                    sx={textFieldSx}>
                                    <MenuItem value="">
                                        <em>Use brand default</em>
                                    </MenuItem>
                                    {TONE_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}>
                                <Typography sx={{fontSize: 14, fontWeight: 700, color: C.ink}}>Audience override</Typography>
                                <Typography sx={{fontSize: 12, color: C.textMuted, lineHeight: 1.5}}>
                                    Leave empty to inherit from your brand profile. Set a value to target a specific audience for this use case.
                                </Typography>
                                <TextField
                                    label={mergedSettings.audienceOverride ? 'Overridden' : 'Inherited from brand'}
                                    size="small"
                                    value={mergedSettings.audienceOverride || ''}
                                    onChange={(event) =>
                                        setSettings((s) => ({...mergeProfileSettings(s), audienceOverride: event.target.value || null}))
                                    }
                                    placeholder="e.g. B2B buyers, contractors"
                                    sx={textFieldSx}
                                />
                            </Box>
                        </>
                    )}

                    {activeTab === 'bullets' && (
                        <>
                            <Box sx={gridSx}>
                                <TextField
                                    label="Bullet point count"
                                    type="number"
                                    size="small"
                                    value={mergedSettings.bullets.bulletCount}
                                    inputProps={{min: 1, max: 20}}
                                    onChange={(event) =>
                                        updateSection(
                                            'bullets',
                                            'bulletCount',
                                            clamp(Number(event.target.value || 1), 1, 20),
                                        )
                                    }
                                    sx={textFieldSx}
                                />
                                <TextField
                                    select
                                    label="Heading style"
                                    size="small"
                                    value={mergedSettings.bullets.headingStyle}
                                    onChange={(event) =>
                                        updateSection('bullets', 'headingStyle', event.target.value)
                                    }
                                    sx={textFieldSx}>
                                    {PROFILE_HEADING_STYLE_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>

                            {renderRangeSection(
                                'bullets',
                                'Characters per bullet',
                                'Stored with the profile as a writing constraint. Output count still follows the configured field rows in this app.',
                            )}
                            {renderToneStyleFields('bullets')}
                            <TextField
                                label="Additional context / requirements for AI"
                                multiline
                                minRows={4}
                                value={mergedSettings.bullets.profileContext}
                                onChange={(event) =>
                                    updateSection('bullets', 'profileContext', event.target.value.slice(0, PROFILE_CONTEXT_MAX_CHARS))
                                }
                                placeholder="Bullet-specific instructions"
                                helperText={`${(mergedSettings.bullets.profileContext || '').length}/${PROFILE_CONTEXT_MAX_CHARS}`}
                                sx={textFieldSx}
                            />
                        </>
                    )}

                    {activeTab === 'faqs' && (
                        <>
                            <TextField
                                label="FAQ count"
                                type="number"
                                size="small"
                                value={mergedSettings.faqs.faqCount}
                                inputProps={{min: FAQ_COUNT_MIN, max: FAQ_COUNT_MAX}}
                                onChange={(event) =>
                                    updateSection(
                                        'faqs',
                                        'faqCount',
                                        clamp(
                                            Number(event.target.value || FAQ_COUNT_MIN),
                                            FAQ_COUNT_MIN,
                                            FAQ_COUNT_MAX,
                                        ),
                                    )
                                }
                                helperText="Default FAQs to generate per product. The generator's stepper can still override this for a single run."
                                sx={textFieldSx}
                            />
                            {renderFaqRangePair({
                                label: 'Characters per question',
                                minKey: 'minQuestionChars',
                                maxKey: 'maxQuestionChars',
                                cap: FAQ_QUESTION_CAP,
                                helperText:
                                    'Soft target for each FAQ question. Keep short and natural — buyers ask "How do I…" not "Specifications regarding…".',
                            })}
                            {renderFaqRangePair({
                                label: 'Characters per answer',
                                minKey: 'minAnswerChars',
                                maxKey: 'maxAnswerChars',
                                cap: FAQ_ANSWER_CAP,
                                helperText:
                                    'Soft target for each FAQ answer. The first sentence should always be the direct answer (answer-first rule is baked into the prompt regardless of length).',
                            })}
                            {renderToneStyleFields('faqs')}
                            <TextField
                                label="Additional context / requirements for AI"
                                multiline
                                minRows={4}
                                value={mergedSettings.faqs.profileContext}
                                onChange={(event) =>
                                    updateSection(
                                        'faqs',
                                        'profileContext',
                                        event.target.value.slice(0, PROFILE_CONTEXT_MAX_CHARS),
                                    )
                                }
                                placeholder="FAQ-specific instructions (e.g. always mention 60-day returns when relevant)"
                                helperText={`${(mergedSettings.faqs.profileContext || '').length}/${PROFILE_CONTEXT_MAX_CHARS}`}
                                sx={textFieldSx}
                            />
                        </>
                    )}

                    {activeTab === 'description' && (
                        <>
                            {renderRangeSection(
                                'description',
                                'Description length',
                                'Used as a soft character range for generated descriptions.',
                            )}
                            {renderToneStyleFields('description')}
                            <TextField
                                label="Additional context / requirements for AI"
                                multiline
                                minRows={4}
                                value={mergedSettings.description.profileContext}
                                onChange={(event) =>
                                    updateSection(
                                        'description',
                                        'profileContext',
                                        event.target.value.slice(0, PROFILE_CONTEXT_MAX_CHARS),
                                    )
                                }
                                placeholder="Description-specific instructions"
                                helperText={`${(mergedSettings.description.profileContext || '').length}/${PROFILE_CONTEXT_MAX_CHARS}`}
                                sx={textFieldSx}
                            />
                        </>
                    )}

                    {activeTab === 'title' && (
                        <>
                            {renderRangeSection(
                                'title',
                                'Title length',
                                'Used as a guidance range for generated titles.',
                            )}
                            {renderToneStyleFields('title')}
                            <TextField
                                label="Additional context / requirements for AI"
                                multiline
                                minRows={4}
                                value={mergedSettings.title.profileContext}
                                onChange={(event) =>
                                    updateSection('title', 'profileContext', event.target.value.slice(0, PROFILE_CONTEXT_MAX_CHARS))
                                }
                                placeholder="Title-specific instructions"
                                helperText={`${(mergedSettings.title.profileContext || '').length}/${PROFILE_CONTEXT_MAX_CHARS}`}
                                sx={textFieldSx}
                            />
                        </>
                    )}
                </Box>

                <TextField
                    fullWidth
                    label="Profile name"
                    value={profileName}
                    onChange={(event) => setProfileName(event.target.value)}
                    placeholder="Profile name"
                    sx={{
                        ...textFieldSx,
                        mt: 2,
                    }}
                />
            </DialogContent>

            <DialogActions
                sx={{
                    px: 3,
                    py: 2,
                    borderTop: `1px solid ${C.border}`,
                    backgroundColor: C.surfaceAlt,
                }}>
                <Button onClick={onClose} color="inherit">
                    Close
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={!profileName.trim()}>
                    Save Profile
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProfileSettingsDialog;
